import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Switch,
    Typography,
} from "@material-ui/core";
import { Controller, useForm } from "react-hook-form";
import { AddCircle } from "@material-ui/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router-dom";
import { useModalStyles } from "./styles";
import {
    useAttendanceLocalDepartmentCreate,
    AttendanceLocalDepartmentCreateData,
} from "../../../../../../../../../services/api-tansquery/attendance-local/department/create";
import { createDeparmentSchema, CreateDepartmentSchemaData } from "./schema";
import DepartmentField from "./components/department-field";

type NewUserProps = {
    open: boolean;
    hasMistoDepartment: boolean;
    handleCloser: () => void;
};

export default function NewDepartment({
    open,
    hasMistoDepartment,
    handleCloser,
}: NewUserProps) {
    const { slug } = useParams<{ slug: string }>();
    const { isLoading, mutate: createDepartmentFn } =
        useAttendanceLocalDepartmentCreate();

    const { watch, control, reset, handleSubmit } = useForm<CreateDepartmentSchemaData>({
        resolver: zodResolver(createDeparmentSchema),
        defaultValues: {
            unidade_slug: slug,
            misto: false,
            ativo: false,
            setor_id: null
        }
    });

    const { classes } = useModalStyles();

    const hasMisto = watch("misto");

    const handleCreate = (data: CreateDepartmentSchemaData) => {
        const { setor_id } = data;
        const requestPayload: AttendanceLocalDepartmentCreateData = {
            unidade_slug: slug,
            misto: data.misto,
            ativo: data.ativo,
            setor_id: data.misto ? undefined : setor_id.id,
        };
        createDepartmentFn(requestPayload, {
            onSuccess: () => {
                handleCloser();
                reset();
            },
        });
    };

    function handleClose() {
        handleCloser();
        reset();
    }

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography className={classes.title}>
                <AddCircle className={classes.icon} />
                <Typography>Setor</Typography>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <form onSubmit={handleSubmit(handleCreate)}>
                    {!hasMisto && (
                        <FormControl
                            className={classes.form}
                            required
                            variant="outlined"
                            fullWidth
                        >
                            <Controller
                                name="setor_id"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <DepartmentField
                                        slug={slug}
                                        field={field}
                                        fieldState={fieldState}
                                        style={{ flex: 3 }}
                                    />
                                )}
                            />
                        </FormControl>
                    )}
                    <Controller
                        name="ativo"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Switch {...field} checked={field.value} />}
                                label="Este setor está ativo?"
                            />
                        )}
                    />
                    <FormHelperText>
                        Ative esta opção para permitir que este setor seja exibido como
                        disponível.
                    </FormHelperText>
                    {hasMistoDepartment && (
                        <Box mt={2}>
                            <Controller
                                name="misto"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch {...field} checked={field.value} />}
                                        label="Setor Misto"
                                    />
                                )}
                            />
                            <FormHelperText>
                                Marque se o setor for misto (atende vários serviços).
                            </FormHelperText>
                        </Box>
                    )}
                    <Box className={classes.contentActions}>
                        <Button
                            className={classes.cancelButton}
                            disabled={isLoading}
                            onClick={handleClose}
                        >
                            Cancelar
                        </Button>
                        <Button
                            className={classes.confirmButton}
                            disabled={isLoading}
                            variant="contained"
                            type="submit"
                        >
                            {isLoading ? <CircularProgress /> : "Salvar"}
                        </Button>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
}
